/**
 * ExportQueueManager - Manages export queue via API
 * 
 * Handles:
 * - Fetching export queue from server
 * - Adding photos to export queue
 * - Removing photos from export queue
 * - Updating export item status
 * - Clearing completed items
 */

export class ExportQueueManager {
  constructor(editorState) {
    this.editorState = editorState;
  }

  /**
   * Get CSRF token from meta tag
   */
  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  /**
   * Fetch all export queue items from server
   */
  async fetchQueue() {
    try {
      const response = await fetch('/api/v1/export_queue_items');
      if (!response.ok) {
        throw new Error(`Failed to fetch export queue: ${response.status}`);
      }
      
      const items = await response.json();
      this.editorState.set({ exportQueueItems: items });
      console.log(`[ExportQueueManager] Fetched ${items.length} queue items`);
      return items;
    } catch (error) {
      console.error('[ExportQueueManager] Error fetching queue:', error);
      throw error;
    }
  }

  /**
   * Add photo(s) to export queue
   * @param {number|number[]} photoIds - Single photo ID or array of photo IDs
   * @param {object} settings - Export settings (slider values, inference steps, etc.)
   * @param {string} filenamePattern - Filename pattern (default: '{filename}_tg')
   */
  async addToQueue(photoIds, settings = {}, filenamePattern = null) {
    try {
      // Ensure photoIds is an array
      const ids = Array.isArray(photoIds) ? photoIds : [photoIds];
      
      // Get current state for settings
      const state = this.editorState.get();
      const exportSettings = {
        ...settings,
        resolution: state.fullResolutionSize || 1024,
        num_inference_steps: state.fullResInferenceSteps || 28,
        seed: state.seed || 42,
        sample_every: 0  // Only get final image for exports
      };
      
      const pattern = filenamePattern || state.exportFilenamePattern || '{filename}_tg';
      
      const response = await fetch('/api/v1/export_queue_items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          photo_ids: ids,
          settings: exportSettings,
          filename_pattern: pattern
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add to export queue: ${response.status}`);
      }
      
      const newItems = await response.json();
      
      // Add new items to state
      const currentItems = state.exportQueueItems || [];
      this.editorState.set({ 
        exportQueueItems: [...currentItems, ...newItems]
      });
      
      console.log(`[ExportQueueManager] Added ${newItems.length} items to queue`);
      return newItems;
    } catch (error) {
      console.error('[ExportQueueManager] Error adding to queue:', error);
      throw error;
    }
  }

  /**
   * Update export queue item status
   * @param {number} itemId - Queue item ID
   * @param {string} status - New status ('pending'|'rendering'|'completed'|'failed')
   * @param {string} errorMessage - Error message if failed
   */
  async updateStatus(itemId, status, errorMessage = null) {
    console.log(`[ExportQueueManager] >>> updateStatus called: itemId=${itemId}, status=${status}`);
    try {
      console.log(`[ExportQueueManager] Sending PATCH to server...`);
      const response = await fetch(`/api/v1/export_queue_items/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          status: status,
          error_message: errorMessage
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update item status: ${response.status}`);
      }
      
      console.log(`[ExportQueueManager] Server responded, parsing JSON...`);
      const updatedItem = await response.json();
      
      // Update item in state
      console.log(`[ExportQueueManager] Updating state...`);
      const state = this.editorState.get();
      const items = state.exportQueueItems.map(item => 
        item.id === itemId ? updatedItem : item
      );
      console.log(`[ExportQueueManager] Calling editorState.set with ${items.length} items`);
      this.editorState.set({ exportQueueItems: items });
      console.log(`[ExportQueueManager] <<< updateStatus complete`);
      
      return updatedItem;
    } catch (error) {
      console.error('[ExportQueueManager] Error updating status:', error);
      throw error;
    }
  }

  /**
   * Remove item from export queue
   * @param {number} itemId - Queue item ID
   */
  async removeFromQueue(itemId) {
    try {
      const response = await fetch(`/api/v1/export_queue_items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.getCSRFToken()
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to remove from queue: ${response.status}`);
      }
      
      // Remove item from state
      const state = this.editorState.get();
      const items = state.exportQueueItems.filter(item => item.id !== itemId);
      this.editorState.set({ exportQueueItems: items });
      
      console.log(`[ExportQueueManager] Removed item ${itemId} from queue`);
    } catch (error) {
      console.error('[ExportQueueManager] Error removing from queue:', error);
      throw error;
    }
  }

  /**
   * Clear all completed items from queue
   */
  async clearCompleted() {
    try {
      const response = await fetch('/api/v1/export_queue_items/clear_completed', {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.getCSRFToken()
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to clear completed items: ${response.status}`);
      }
      
      // Remove completed items from state
      const state = this.editorState.get();
      const items = state.exportQueueItems.filter(item => item.status !== 'completed');
      this.editorState.set({ exportQueueItems: items });
      
      console.log('[ExportQueueManager] Cleared completed items');
    } catch (error) {
      console.error('[ExportQueueManager] Error clearing completed:', error);
      throw error;
    }
  }

  /**
   * Get queue statistics
   * @returns {object} Stats object with counts by status
   */
  getQueueStats() {
    const state = this.editorState.get();
    const items = state.exportQueueItems || [];
    
    return {
      total: items.length,
      pending: items.filter(i => i.status === 'pending').length,
      rendering: items.filter(i => i.status === 'rendering').length,
      completed: items.filter(i => i.status === 'completed').length,
      failed: items.filter(i => i.status === 'failed').length
    };
  }
}

